import { useState } from "react";
import { Sun, Moon, Trash2, Download } from "lucide-react";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/Button";
import { useToast } from "@/components/Toast";
import { cn } from "@/lib/cn";

export default function Settings() {
  const { theme, setTheme, reset, user } = useStore();
  const toast = useToast((s) => s.push);

  const [notifPrefs, setNotifPrefs] = useState({
    forks: true, comments: true, challenges: true, evidence: false,
    crux_contested: true, mind_changes: true,
  });

  const toggle = (k: keyof typeof notifPrefs) => setNotifPrefs((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-semibold tracking-tight mb-5">Settings</h1>

      <Section title="Appearance">
        <div className="flex items-center gap-2">
          <Button variant={theme === "dark" ? "primary" : "outline"} size="sm" onClick={() => setTheme("dark")} leftIcon={<Moon size={13} />}>Dark</Button>
          <Button variant={theme === "light" ? "primary" : "outline"} size="sm" onClick={() => setTheme("light")} leftIcon={<Sun size={13} />}>Light</Button>
          <span className="text-[12px] text-[var(--color-ink-muted)] ml-2">Dark is the default — research-lab feel.</span>
        </div>
      </Section>

      <Section title="Profile">
        <div className="space-y-2">
          <Row label="username" value={user?.username ?? "not signed in"} />
          <Row label="display name" value={user?.display_name ?? "—"} />
          <Row label="bio" value={user?.bio ?? "—"} />
        </div>
      </Section>

      <Section title="Notification preferences">
        <div className="space-y-1.5">
          {Object.entries(notifPrefs).map(([k, v]) => (
            <button
              key={k}
              onClick={() => toggle(k as any)}
              className="flex items-center justify-between w-full py-2 px-3 rounded-lg hover:bg-[var(--color-surface-2)] transition-colors text-left"
            >
              <span className="text-[13px] text-[var(--color-ink-soft)] capitalize">{k.replace(/_/g, " ")}</span>
              <span className={cn("w-9 h-5 rounded-full p-0.5 transition-colors", v ? "bg-[var(--color-primary)]" : "bg-[var(--color-surface-3)]")}>
                <span className={cn("block w-4 h-4 rounded-full bg-white transition-transform", v && "translate-x-4")} />
              </span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Default visibility">
        <div className="flex gap-1.5">
          {(["public", "unlisted", "private"] as const).map((v) => (
            <button key={v} className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[13px] text-[var(--color-ink-soft)] hover:border-[var(--color-primary)] transition-colors">
              {v}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Data">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" leftIcon={<Download size={13} />} onClick={() => toast({ tone: "info", title: "Coming soon", desc: "Account export arrives with the backend." })}>
            Export my data
          </Button>
          <Button variant="danger" size="sm" leftIcon={<Trash2 size={13} />} onClick={() => {
            if (confirm("Reset local UI state? This clears cached reactions, stances, and drafts in this browser.")) {
              reset();
              toast({ tone: "ok", title: "State reset", desc: "Browser cache cleared. Backend data is unchanged." });
            }
          }}>
            Reset local state
          </Button>
        </div>
        <p className="text-[11px] text-[var(--color-ink-faint)] mt-2">Strains, users, comments, reactions, stances, forks, and reports are persisted by the Flask/SQLite backend.</p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">{title}</div>
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)] w-28 shrink-0">{label}</span>
      <span className="text-[13px] text-[var(--color-ink-soft)]">{value}</span>
    </div>
  );
}
